import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import Dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import React from 'react'

import {
  createPettyCashBatch,
  deletePettyCash,
  fetchMyCentres,
  fetchPettyCash,
  fetchPettyCashSummary,
  savePettyCashDraft,
  updatePettyCash,
  uploadAttachment
} from 'api/procurement'
import constants from 'app/constants'
import { confirm, eventAlert, notify } from 'app/helpers'

import AttachmentUploader from '../components/AttachmentUploader'
import BudgetBanner from '../components/BudgetBanner'
import { downloadCsv, parseCsvFile } from '../components/exportUtils'
import {
  saveDraftBills,
  loadDraftBills,
  clearDraftBills
} from '../components/draftBills'
import {
  checkOverallBudget,
  overBudgetMessage,
  pettyCashCapMessage,
  softOverspendNote
} from '../components/overallBudget'
import {
  PETTY_CASH_CATEGORIES,
  REPAIR_MAINTENANCE_CATEGORIES,
  RM_PAID_BY_OPTIONS,
  SHOW_CSV_IMPORT,
  orderCentres,
  rmPaidByLabel
} from '../constants'
import SopButton from '../components/SopButton'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  // Page-level sticky header (2026-07-21): headers freeze at the top of the
  // screen on scroll-down for long petty-cash / R&M ledgers.
  stickyHead: {
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 5,
      background: theme.palette.grey[100],
      boxShadow: 'inset 0 -1px 0 #e0ddd6'
    }
  },
  // Hide number-input spinner arrows (error-prone; could wipe values).
  noSpin: {
    '& input[type=number]': { MozAppearance: 'textfield' },
    '& input[type=number]::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0
    }
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  spacer: { flexGrow: 1 },
  select: { minWidth: 160 },
  sectionTitle: { fontWeight: 600, marginBottom: theme.spacing(1) }
}))

// The centre records only spends now; entry_type is fixed server-side.
// The row date follows the month you're viewing: today on the current month,
// otherwise the 1st of that month. The month picker only drives what the ledger
// *reads*, so a row left on today's date while viewing (say) August would be
// filed against today's month and vanish from the ledger in front of you.
const emptyLine = (month) => {
  const today = Dayjs()
  return {
    date:
      month && month !== today.format('YYYY-MM')
        ? `${month}-01`
        : today.format('YYYY-MM-DD'),
    category: '',
    amount: '',
    description: '',
    // R&M only (4 Nov 2026): who paid.
    paid_by: ''
  }
}

// Fixed-width cell so the Paid by column can't widen the ledger.
const PAID_BY_CELL = { width: 140, maxWidth: 140, whiteSpace: 'normal' }

// The R&M-only fields as sent to the API (petty cash ignores them server-side).
const rmFields = (l) => ({ paid_by: l.paid_by || '' })

// Paid by input, shared by the new-line row and the edit dialog. `value` is
// the line, `onChange(patch)` merges into it.
const RmPaidByFields = ({ value, onChange }) => (
  <TextField
    select
    size="small"
    variant="outlined"
    label="Paid by"
    fullWidth
    value={value.paid_by || ''}
    onChange={(e) => onChange({ paid_by: e.target.value })}>
    {RM_PAID_BY_OPTIONS.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        {o.label}
      </MenuItem>
    ))}
  </TextField>
)

// Same component drives both ledgers; `flow` swaps the category list, title and
// the budget it spends against. Repair & Maintenance is just petty cash with a
// different category list (see [[REPAIR_MAINTENANCE_CATEGORIES]]).
const FLOW_CONFIG = {
  petty_cash: {
    title: 'Petty Cash',
    categories: PETTY_CASH_CATEGORIES,
    fileBase: 'petty-cash'
  },
  repair_maintenance: {
    title: 'Repair & Maintenance',
    categories: REPAIR_MAINTENANCE_CATEGORIES,
    fileBase: 'repair-maintenance'
  }
}

const PettyCash = ({
  centreId: controlledCentreId,
  initialMonth,
  flow = 'petty_cash'
} = {}) => {
  const classes = useStyles()
  const cfg = FLOW_CONFIG[flow] || FLOW_CONFIG.petty_cash
  const categories = cfg.categories
  const isRm = flow === 'repair_maintenance'
  // When rendered inside the Center Team layout, the centre is supplied as a
  // controlled prop and the local centre picker is hidden.
  const controlled = !!controlledCentreId

  const [centres, setCentres] = React.useState([])
  const [centreId, setCentreId] = React.useState(controlledCentreId || '')
  const [month, setMonth] = React.useState(
    initialMonth || Dayjs().format('YYYY-MM')
  )
  const [entries, setEntries] = React.useState([])
  const [summary, setSummary] = React.useState(null)
  const [draft, setDraft] = React.useState([emptyLine(month)])
  const [editEntry, setEditEntry] = React.useState(null)
  const [attachEntry, setAttachEntry] = React.useState(null)
  // true when the dialog was opened by View (jump to the bill) rather than
  // by Invoice (manage the files).
  const [attachPreview, setAttachPreview] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)

  const monthDate = `${month}-01`

  React.useEffect(() => {
    if (controlled) {
      setCentreId(controlledCentreId)
      return
    }
    ;(async () => {
      try {
        const res = await fetchMyCentres()
        const list = orderCentres(res?.results || [])
        setCentres(list)
        if (list.length) setCentreId(list[0].uuid)
      } catch (error) {
        eventAlert(
          error?.message || 'Failed to load centres',
          constants.ERROR,
          'Petty Cash'
        )
      }
    })()
  }, [controlled, controlledCentreId])

  const loadData = React.useCallback(async () => {
    if (!centreId) return
    setLoading(true)
    try {
      const [entryRes, summaryRes] = await Promise.all([
        fetchPettyCash({ centre: centreId, month: monthDate, flow }),
        fetchPettyCashSummary({ centre: centreId, month: monthDate, flow })
      ])
      setEntries(Array.isArray(entryRes) ? entryRes : entryRes?.results || [])
      setSummary(summaryRes)
    } catch (error) {
      eventAlert(
        error?.message || 'Failed to load petty cash',
        constants.ERROR,
        'Petty Cash'
      )
    } finally {
      setLoading(false)
    }
  }, [centreId, monthDate, flow])

  React.useEffect(() => {
    loadData()
  }, [loadData])

  // Restore any locally-saved draft rows for this centre/month/flow.
  React.useEffect(() => {
    if (!centreId) return
    let cancelled = false
    try {
      const key = `oh-pc-draft-${centreId}-${monthDate}-${flow}`
      const raw = window.localStorage.getItem(key)
      const rows = raw ? JSON.parse(raw) : null
      if (Array.isArray(rows) && rows.length) {
        setDraft(rows.map((r) => ({ ...emptyLine(month), ...r })))
        // Bill photos saved with the draft live in IndexedDB (localStorage
        // can't hold files) — put them back on their rows once read.
        loadDraftBills(key)
          .then((filesPerRow) => {
            if (cancelled || !filesPerRow.some((fs) => fs.length)) return
            setDraft((prev) =>
              prev.map((l, idx) =>
                filesPerRow[idx] && filesPerRow[idx].length && !draftFiles(l).length
                  ? { ...l, file: undefined, files: filesPerRow[idx] }
                  : l
              )
            )
          })
          .catch(() => {})
      } else {
        // No saved draft for this centre/month/flow. Don't blow away in-progress
        // rows just because the month/centre toggled — that would silently drop
        // typed lines and staged bill photos (which are never persisted). Keep
        // any row the user has touched and only re-date the blank ones to the new
        // month; fall back to one clean line when there's genuinely nothing.
        setDraft((prev) => {
          const touched = prev.filter(
            (l) => l.amount || l.category || l.description || draftFiles(l).length
          )
          if (!touched.length) return [emptyLine(month)]
          const base = emptyLine(month).date
          return prev.map((l) =>
            l.amount || l.category || l.description || draftFiles(l).length
              ? l
              : { ...l, date: base }
          )
        })
      }
    } catch (e) {
      /* ignore a corrupt draft */
    }
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line
  }, [centreId, monthDate, flow])

  // ---- draft (multi-line new submission) ----
  const updateDraft = (i, patch) =>
    setDraft((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)))
  const addDraftLine = () => setDraft((prev) => [...prev, emptyLine(month)])
  const removeDraftLine = (i) =>
    setDraft((prev) =>
      prev.length === 1 ? [emptyLine(month)] : prev.filter((_, idx) => idx !== i)
    )
  // Bills staged against a draft row. Older single-file drafts (and anything
  // restored from an earlier session) may still carry `file`, so read through a
  // helper rather than assuming the array.
  const draftFiles = (l) => l.files || (l.file ? [l.file] : [])
  const addDraftFiles = (i, picked) =>
    setDraft((prev) =>
      prev.map((l, idx) =>
        idx === i ? { ...l, file: undefined, files: [...draftFiles(l), ...picked] } : l
      )
    )
  const removeDraftFile = (i, fi) =>
    setDraft((prev) =>
      prev.map((l, idx) =>
        idx === i
          ? { ...l, file: undefined, files: draftFiles(l).filter((_, x) => x !== fi) }
          : l
      )
    )

  // P5 phase 3: deterministic CSV import. Parsed rows land in the editable draft
  // (the human confirm step) — nothing is saved until the user hits Submit.
  const onUploadCsv = async (e) => {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file) return
    try {
      const data = await parseCsvFile(file)
      const parsed = data
        .map((r) => {
          const rawDate = r.date ?? r['Date'] ?? ''
          const d = Dayjs(rawDate)
          return {
            date: d.isValid() ? d.format('YYYY-MM-DD') : '',
            category: (r.category || r['Category'] || '').trim(),
            amount: r.amount ?? r['Amount'] ?? '',
            description: (r.description || r['Description'] || '').trim()
          }
        })
        .filter((l) => Number(l.amount) > 0)
      if (!parsed.length) {
        notify('No rows with an amount found in the CSV', '', 'warning')
        return
      }
      setDraft((prev) => [
        ...prev.filter((l) => Number(l.amount) > 0),
        ...parsed
      ])
      notify(
        `${parsed.length} line(s) added from CSV — review and Submit`,
        '',
        'success'
      )
    } catch (err) {
      eventAlert(
        err?.message || 'Could not read CSV',
        constants.ERROR,
        'Petty Cash'
      )
    }
  }

  // Template mirrors the on-screen columns (Date, Category, Amount, Description)
  // so an imported file maps 1:1 to what's entered in the app. Category is
  // mandatory; the sample row shows a valid one for this ledger.
  const downloadTemplate = () =>
    downloadCsv(
      `${cfg.fileBase}-template`,
      ['Date', 'Category', 'Amount', 'Description'],
      [[Dayjs().format('YYYY-MM-DD'), categories[0] || '', 0, '']]
    )

  // Local (this-device) draft of the in-progress rows so they aren't lost on
  // navigation. Petty cash counts only what's Submitted (with a bill), so a
  // draft is purely a convenience stash. Row text goes to localStorage (+ a
  // server copy for HQ visibility); the staged bill photos are kept in
  // IndexedDB on the same device (components/draftBills.js).
  const draftKey = `oh-pc-draft-${centreId}-${monthDate}-${flow}`

  const onSaveDraft = async () => {
    if (!centreId) {
      notify('Select a centre', '', 'warning')
      return
    }
    const kept = draft.filter((l) => l.amount || l.description || l.category)
    const rows = kept.map((l) => ({
        date: l.date,
        category: l.category || '',
        amount: l.amount,
        description: l.description || '',
        ...(isRm ? rmFields(l) : {})
      }))
    try {
      if (rows.length) {
        window.localStorage.setItem(draftKey, JSON.stringify(rows))
      } else {
        window.localStorage.removeItem(draftKey)
      }
    } catch (e) {
      /* private mode etc. — the server copy below still goes through */
    }
    // Bills can't go in localStorage or the server draft — keep them in
    // IndexedDB on this device, aligned with the saved rows.
    const billCount = kept.reduce((n, l) => n + draftFiles(l).length, 0)
    let billsKept = true
    try {
      await saveDraftBills(draftKey, kept.map((l) => draftFiles(l)))
    } catch (e) {
      billsKept = !billCount
    }
    // Also sync the draft to the server so HQ sees planned spend (the local
    // copy is the editing safety net; the server copy is for visibility).
    try {
      await savePettyCashDraft({
        centre: centreId,
        month: monthDate,
        flow,
        lines: rows
      })
      if (billsKept) {
        notify('Draft saved — HQ can see it as planned spend', '', 'success')
      } else {
        notify(
          'Draft saved, but the bill photos could not be kept on this device — re-attach them before submitting',
          '',
          'warning'
        )
      }
    } catch (err) {
      notify(
        'Draft saved on this device, but syncing it to HQ failed — try Save draft again',
        '',
        'warning'
      )
    }
  }

  const onSubmit = async () => {
    if (!centreId) {
      notify('Select a centre', '', 'warning')
      return
    }
    // Keep the staged invoice files alongside each submittable line (same order)
    // so we can attach them to the created entry after the batch saves.
    const submittable = draft.filter((l) => Number(l.amount) > 0)
    const lines = submittable.map((l) => ({
      date: l.date,
      category: l.category || '',
      amount: Number(l.amount),
      description: l.description,
      ...(isRm ? rmFields(l) : {})
    }))
    const fileGroups = submittable.map((l) => draftFiles(l))
    if (isEmpty(lines)) {
      notify('Add at least one line with an amount', '', 'warning')
      return
    }
    // An entry belongs to the month of its own date, not the month on screen —
    // the server files it (and tests it against that month's budget) by
    // line.date. Block the mismatch rather than let the row disappear into
    // another month's ledger.
    const strayDates = lines
      .filter((l) => Dayjs(l.date).format('YYYY-MM') !== month)
      .map((l) => Dayjs(l.date).format('D MMM YYYY'))
    if (strayDates.length) {
      eventAlert(
        `You are viewing ${Dayjs(monthDate).format('MMMM YYYY')}, but ` +
          `${strayDates.length} entr${
            strayDates.length > 1 ? 'ies are' : 'y is'
          } dated ${strayDates.join(', ')}. An entry is saved against its own ` +
          `date, so ${
            strayDates.length > 1 ? 'these' : 'this'
          } would not appear in this month's ledger. Fix the date${
            strayDates.length > 1 ? 's' : ''
          }, or switch to that month first.`,
        constants.ERROR,
        'Entry dated outside this month'
      )
      return
    }
    // Category is mandatory so spends are classified.
    if (lines.some((l) => !l.category)) {
      notify('Pick a category for every entry before submitting', '', 'warning')
      return
    }
    if (isRm && lines.some((l) => !l.paid_by)) {
      notify('Pick who paid (Paid by) for every entry before submitting', '', 'warning')
      return
    }
    // Supporting bill is mandatory: every entry needs at least one invoice.
    if (fileGroups.some((g) => !g.length)) {
      notify(
        'Attach the supporting bill (📎 Invoice) for every entry before submitting',
        '',
        'warning'
      )
      return
    }
    const doSave = async () => {
      setSaving(true)
      try {
        const res = await createPettyCashBatch({ centre: centreId, lines, flow })
        // Upload the staged invoices to their newly-created entries (matched by
        // submit order). Entries are already saved, so a failed upload only
        // costs that one attachment — surfaced, not fatal.
        const created = res?.results || []
        let attachFails = 0
        for (let i = 0; i < created.length; i += 1) {
          const group = fileGroups[i] || []
          for (let f = 0; f < group.length; f += 1) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await uploadAttachment(
                'petty_cash_entry',
                { entry: created[i].uuid },
                group[f]
              )
            } catch (e) {
              attachFails += 1
            }
          }
        }
        notify(
          `${lines.length} entr${lines.length > 1 ? 'ies' : 'y'} added` +
            (attachFails ? ` (${attachFails} invoice upload(s) failed)` : ''),
          '',
          attachFails ? 'warning' : 'success'
        )
        setDraft([emptyLine(month)])
        try {
          window.localStorage.removeItem(draftKey)
        } catch (e) {
          /* ignore */
        }
        clearDraftBills(draftKey)
        await loadData()
      } catch (error) {
        eventAlert(error?.message || 'Failed to save', constants.ERROR, 'Petty Cash')
      } finally {
        setSaving(false)
      }
    }

    // P5: a single category may overspend — warn but allow. The overall month
    // budget (all categories) is the hard cap (blocks).
    const addTotal = lines.reduce((s, l) => s + Number(l.amount), 0)
    const projected = Number(summary?.month_spent || 0) + addTotal
    // Hard cap: block if the month's total across all categories goes over. The
    // budget category matches the flow ('petty_cash' / 'repair_maintenance').
    const ob = await checkOverallBudget({
      centre: centreId,
      month: monthDate,
      category: flow,
      projectedCategoryValue: projected
    })
    if (ob.exceeded) {
      eventAlert(
        overBudgetMessage(ob, Dayjs(monthDate).format('MMMM YYYY'), 'spend'),
        constants.ERROR,
        'Month budget exceeded'
      )
      return
    }
    if (
      summary &&
      summary.budget != null &&
      projected > Number(summary.budget)
    ) {
      // Petty cash is a hard cap (server: assert_petty_cash_within_budget), so
      // it stops here rather than asking — the centre would otherwise confirm
      // and then take a 400. R&M shares this page and stays soft.
      if (flow === 'petty_cash') {
        eventAlert(
          pettyCashCapMessage({
            budget: summary.budget,
            already: summary.month_spent,
            adding: addTotal,
            monthLabel: Dayjs(monthDate).format('MMMM YYYY')
          }),
          constants.ERROR,
          'Petty cash budget reached'
        )
        return
      }
      confirm(doSave, softOverspendNote('repair & maintenance'), () => {})
      return
    }
    await doSave()
  }

  // ---- edit existing entry (dialog) ----
  const saveEdit = async () => {
    try {
      await updatePettyCash(editEntry.uuid, {
        date: editEntry.date,
        entry_type: editEntry.entry_type,
        category: editEntry.category || '',
        amount: Number(editEntry.amount),
        description: editEntry.description || '',
        ...(isRm ? rmFields(editEntry) : {})
      })
      notify('Entry updated', '', 'success')
      setEditEntry(null)
      await loadData()
    } catch (error) {
      eventAlert(error?.message || 'Update failed', constants.ERROR, 'Petty Cash')
    }
  }

  // Open the attached bill directly (fetch a fresh short-lived URL on click —
  // they expire in ~5 min, so we don't pre-mint them for the whole list). Falls
  // back to the Invoice dialog if the direct link can't be fetched.
  // View opens the bill in the same dialog the Invoice button uses, with the
  // viewer already showing it. It used to window.open() the attachment's `url`,
  // which is the SAVE link (Content-Disposition: attachment) — so clicking View
  // downloaded the file instead of showing it. `view_url` is the inline one, and
  // the uploader's viewer already prefers it.
  const onViewInvoice = (entry) => {
    setAttachEntry(entry)
    setAttachPreview(true)
  }

  const onDelete = (uuid) => {
    confirm(
      async () => {
        try {
          await deletePettyCash(uuid)
          notify('Entry deleted', '', 'success')
          await loadData()
        } catch (error) {
          eventAlert(error?.message || 'Delete failed', constants.ERROR, 'Petty Cash')
        }
      },
      'Delete this petty cash entry?',
      () => {}
    )
  }

  const frozen = !!summary?.frozen

  // Typed-but-unsubmitted rows (including a restored saved draft) — surfaced in
  // the budget banner so utilization doesn't read as untouched while a draft is
  // sitting on screen. Backend `used` stays submitted-only.
  const draftTotal = frozen
    ? 0
    : draft.reduce(
        (sum, l) => sum + (Number(l.amount) > 0 ? Number(l.amount) : 0),
        0
      )

  return (
    <Paper className={`${classes.root} ${classes.noSpin}`} elevation={0}>
      <div className={classes.toolbar}>
        {!controlled && <Typography variant="h6">{cfg.title}</Typography>}
        {!controlled && (
          <TextField
            select
            className={classes.select}
            size="small"
            variant="outlined"
            label="Centre"
            value={centreId}
            onChange={(e) => setCentreId(e.target.value)}>
            {centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          type="month"
          label="Month"
          size="small"
          variant="outlined"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <div className={classes.spacer} />
        {/* CSV import + template sit in the top-right cluster. */}
        {!frozen && (
          <>
            {SHOW_CSV_IMPORT && (
              <>
                <Button size="small" variant="outlined" component="label">
                  Upload CSV
                  <input
                    type="file"
                    hidden
                    accept=".csv"
                    onChange={onUploadCsv}
                  />
                </Button>
                <Button size="small" onClick={downloadTemplate}>
                  Template
                </Button>
              </>
            )}
            {/* Save draft + Submit sit up top, matching Program Material and
                Consumables for a consistent layout. */}
            <SopButton
              category={
                flow === 'repair_maintenance' ? 'repair_maintenance' : 'petty_cash'
              }
            />
            <Button
              size="small"
              variant="outlined"
              color="primary"
              disabled={saving}
              onClick={onSaveDraft}>
              Save draft
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={saving}
              onClick={onSubmit}>
              {saving ? 'Saving…' : 'Submit'}
            </Button>
          </>
        )}
      </div>


      {summary && (
        <BudgetBanner
          label="This month (spent)"
          budget={summary.budget}
          used={summary.month_spent}
          remaining={summary.remaining}
          inDraft={draftTotal}
          // Petty cash is the one category that cannot be overspent; R&M
          // shares this page and stays soft.
          hardCap={flow === 'petty_cash'}
        />
      )}

      {frozen && (
        <Box
          mb={2}
          p={1.5}
          style={{ background: '#ede7f6', borderRadius: 8, color: '#5e35b1' }}>
          This month is <strong>frozen</strong> (petty cash locks on the 3rd of the
          following month) — entries can no longer be added or edited. Ask HQ to
          unlock it if you still need to make changes.
        </Box>
      )}

      {/* Unified ledger: saved rows + inline editable new rows in one view */}
      <Typography className={classes.sectionTitle}>
        Ledger — {Dayjs(monthDate).format('MMMM YYYY')}
      </Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer style={{ overflow: 'visible' }}>
          <Table size="small" className={classes.stickyHead}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 56 }}>Sl No.</TableCell>
                <TableCell style={{ width: 140 }}>Date</TableCell>
                <TableCell style={{ width: 200 }}>Category</TableCell>
                <TableCell align="right" style={{ width: 110 }}>
                  Amount (₹)
                </TableCell>
                <TableCell>Description</TableCell>
                {isRm && <TableCell style={PAID_BY_CELL}>Paid by</TableCell>}
                <TableCell style={{ width: 150 }}>Entered by</TableCell>
                <TableCell align="center" style={{ width: 150 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* saved rows */}
              {entries.map((e, i) => (
                <TableRow key={e.uuid} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{Dayjs(e.date).format('D MMMM YYYY')}</TableCell>
                  <TableCell>{e.category || '—'}</TableCell>
                  <TableCell align="right">
                    {Number(e.amount).toLocaleString('en-IN')}
                  </TableCell>
                  <TableCell>{e.description}</TableCell>
                  {isRm && (
                    <TableCell style={PAID_BY_CELL}>
                      {rmPaidByLabel(e.paid_by)}
                    </TableCell>
                  )}
                  <TableCell>{e.created_by_name || '—'}</TableCell>
                  <TableCell align="center">
                    {!frozen && (
                      <>
                        <Button
                          size="small"
                          onClick={() =>
                            setEditEntry({ ...e, amount: String(e.amount) })
                          }>
                          Edit
                        </Button>
                        <IconButton size="small" onClick={() => onDelete(e.uuid)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                    {/* At-a-glance 📎 when a bill is attached — opens it directly
                        in a new tab. The Invoice button manages/adds. */}
                    {e.invoice_count > 0 && (
                      <Button
                        size="small"
                        title="View attached bill"
                        onClick={() => onViewInvoice(e)}>
                        📎 View
                      </Button>
                    )}
                    {/* View / attach the bill for this spend line (view stays
                        available when frozen; only adding is blocked). */}
                    <Button
                      size="small"
                      onClick={() => {
                        setAttachEntry(e)
                        setAttachPreview(false)
                      }}
                    >
                      Invoice
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

              {/* inline new rows (continue the serial numbers) */}
              {!frozen &&
                draft.map((l, i) => (
                  <TableRow key={`draft-${i}`} style={{ background: '#fafafa' }}>
                    <TableCell>{entries.length + i + 1}</TableCell>
                    <TableCell>
                      <TextField
                        type="date"
                        size="small"
                        variant="outlined"
                        style={{ width: 140 }}
                        value={l.date}
                        onChange={(e) => updateDraft(i, { date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        variant="outlined"
                        fullWidth
                        label="Category"
                        value={l.category || ''}
                        onChange={(e) =>
                          updateDraft(i, { category: e.target.value })
                        }>
                        {categories.map((c) => (
                          <MenuItem key={c} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        size="small"
                        variant="outlined"
                        placeholder="0"
                        style={{ width: 100 }}
                        value={l.amount}
                        onChange={(e) => updateDraft(i, { amount: e.target.value })}
                        inputProps={{ min: 0, style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        variant="outlined"
                        fullWidth
                        placeholder="Description"
                        value={l.description}
                        onChange={(e) =>
                          updateDraft(i, { description: e.target.value })
                        }
                      />
                    </TableCell>
                    {isRm && (
                      <TableCell style={PAID_BY_CELL}>
                        <RmPaidByFields
                          value={l}
                          onChange={(patch) => updateDraft(i, patch)}
                        />
                      </TableCell>
                    )}
                    {/* Not saved yet, so it will carry the current user's
                        name once submitted. */}
                    <TableCell style={{ color: '#9e9e9e' }}>You</TableCell>
                    <TableCell align="center">
                      {/* A spend can have several bills (multi-page, or one shop
                          per slip), so these pickers ADD to the row rather than
                          replace what's already staged. */}
                      <Button size="small" component="label" title="Attach invoices">
                        {draftFiles(l).length
                          ? `📎 Add (${draftFiles(l).length})`
                          : '📎 Invoice'}
                        <input
                          type="file"
                          hidden
                          multiple
                          accept="image/*,application/pdf"
                          onChange={(e) => {
                            const picked = Array.from(e.target.files || [])
                            e.target.value = ''
                            if (picked.length) addDraftFiles(i, picked)
                          }}
                        />
                      </Button>
                      {/* Camera capture: on a phone this opens the camera to shoot
                          the bill directly; on desktop it falls back to a picker.
                          Same staged-file path as the Invoice upload. */}
                      <Button size="small" component="label" title="Take a photo of the bill">
                        📷 Photo
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => {
                            const picked = Array.from(e.target.files || [])
                            e.target.value = ''
                            if (picked.length) addDraftFiles(i, picked)
                          }}
                        />
                      </Button>
                      <Button size="small" onClick={() => removeDraftLine(i)}>
                        ✕
                      </Button>
                      {draftFiles(l).map((f, fi) => (
                        <Typography
                          key={`${f.name}-${fi}`}
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {f.name}{' '}
                          <Link
                            component="button"
                            type="button"
                            title="Remove this file"
                            onClick={() => removeDraftFile(i, fi)}>
                            ✕
                          </Link>
                        </Typography>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}

              {isEmpty(entries) && (frozen || isEmpty(draft)) && (
                <TableRow>
                  <TableCell colSpan={isRm ? 8 : 7}>
                    <Typography variant="body2" color="textSecondary">
                      No entries for this month.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!frozen && (
        <Box mt={1} mb={3} display="flex" alignItems="center" style={{ gap: 12 }}>
          <Button size="small" variant="outlined" onClick={addDraftLine}>
            + Add line
          </Button>
        </Box>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editEntry} onClose={() => setEditEntry(null)}>
        <DialogTitle>Edit entry</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" style={{ gap: 12, minWidth: 320 }}>
            <TextField
              type="date"
              label="Date"
              size="small"
              variant="outlined"
              value={editEntry?.date || ''}
              onChange={(e) => {
                // React 16 pools the event — read the value before the
                // updater runs, or e.target is null by then.
                const { value } = e.target
                setEditEntry((p) => ({ ...p, date: value }))
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Category"
              size="small"
              variant="outlined"
              value={editEntry?.category || ''}
              onChange={(e) => {
                // React 16 pools the event — read the value before the
                // updater runs, or e.target is null by then.
                const { value } = e.target
                setEditEntry((p) => ({ ...p, category: value }))
              }}>
              {categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="number"
              label="Amount (₹)"
              size="small"
              variant="outlined"
              value={editEntry?.amount || ''}
              onChange={(e) => {
                // React 16 pools the event — read the value before the
                // updater runs, or e.target is null by then.
                const { value } = e.target
                setEditEntry((p) => ({ ...p, amount: value }))
              }}
            />
            <TextField
              label="Description"
              size="small"
              variant="outlined"
              value={editEntry?.description || ''}
              onChange={(e) => {
                // React 16 pools the event — read the value before the
                // updater runs, or e.target is null by then.
                const { value } = e.target
                setEditEntry((p) => ({ ...p, description: value }))
              }}
            />
            {isRm && editEntry && (
              <RmPaidByFields
                value={editEntry}
                onChange={(patch) => setEditEntry((p) => ({ ...p, ...patch }))}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditEntry(null)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={saveEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!attachEntry}
        onClose={() => {
          setAttachEntry(null)
          setAttachPreview(false)
        }}
        maxWidth={attachPreview ? 'md' : 'xs'}
        fullWidth>
        <DialogTitle>
          Supporting document
          {attachEntry
            ? ` — ${Dayjs(attachEntry.date).format('D MMMM YYYY')}, ₹${Number(
                attachEntry.amount
              ).toLocaleString('en-IN')}`
            : ''}
        </DialogTitle>
        <DialogContent>
          {attachEntry ? (
            <AttachmentUploader
              entityType="petty_cash_entry"
              entry={attachEntry.uuid}
              label="Bill / receipt for this spend"
              disabled={frozen}
              autoPreview={attachPreview}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAttachEntry(null)
              setAttachPreview(false)
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default PettyCash
